import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
    CompanyAiUsage,
    CompanyLimitOverrides,
    fetchCompanyAiUsage,
    saveCompanyAiLimits,
    saveCompanyAssistAccess,
    saveCompanyWebSearch,
} from "../services/polarisService";
import AssistAllowedDomainsField from "./AssistAllowedDomainsField";
import { assistAccessNotice, mergeAssistReviewers } from "./assistReviewers";
import { assistLimitFields } from "./assistUsageFields";
import "../styles/polaris-admin.css";

type Period = "daily" | "monthly" | "yearly";
type PageTab = "usage" | "configuration";

function formatUsd(value: number | undefined): string {
    return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(value ?? 0);
}

function toInput(value: number | null | undefined): string {
    return value == null ? "" : String(value);
}

function periodLabel(period: Period): string {
    if (period === "daily") return "Today";
    if (period === "yearly") return "This year";
    return "This month";
}

export default function PolarisCompanyAiUsagePage() {
    const { companyId } = useParams();
    const id = Number(companyId);
    const [tab, setTab] = useState<PageTab>("usage");
    const [period, setPeriod] = useState<Period>("monthly");
    const [data, setData] = useState<CompanyAiUsage | null>(null);
    const [overrides, setOverrides] = useState<CompanyLimitOverrides | null>(null);
    const [assistHidden, setAssistHidden] = useState(false);
    const [evaluators, setEvaluators] = useState<string[]>([]);
    const [evaluatorInput, setEvaluatorInput] = useState("");
    const [webSearchEnabled, setWebSearchEnabled] = useState(true);
    const [allowedDomains, setAllowedDomains] = useState<string[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [notice, setNotice] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);
    const [savingAccess, setSavingAccess] = useState(false);
    const [savingWebSearch, setSavingWebSearch] = useState(false);

    const load = useCallback(async () => {
        if (!Number.isFinite(id)) return;
        setError(null);
        try {
            const result = await fetchCompanyAiUsage(id, period);
            setData(result);
            setOverrides(result.settings.overrides);
            setAssistHidden(result.settings.assistHidden ?? false);
            setEvaluators(result.settings.assistEvaluatorUsernames ?? []);
            setWebSearchEnabled(result.settings.webSearchEnabled ?? true);
            setAllowedDomains(result.settings.allowedDomains ?? []);
        } catch (e: unknown) {
            setError(e instanceof Error ? e.message : "Could not load company AI usage.");
        }
    }, [id, period]);

    useEffect(() => {
        void load();
    }, [load]);

    const parseOverride = (value: string): number | null => {
        const trimmed = value.trim();
        if (!trimmed) return null;
        const parsed = Number.parseInt(trimmed, 10);
        return Number.isFinite(parsed) ? parsed : null;
    };

    const save = async () => {
        if (!overrides) return;
        setSaving(true);
        setError(null);
        setNotice(null);
        try {
            const saved = await saveCompanyAiLimits(id, overrides);
            setData((prev) => (prev ? { ...prev, settings: saved } : prev));
            setOverrides(saved.overrides);
            setNotice("Limits saved. They apply the next time someone asks a question.");
        } catch (e: unknown) {
            setError(e instanceof Error ? e.message : "Could not save company limits.");
        } finally {
            setSaving(false);
        }
    };

    const resetToPlatform = async () => {
        setSaving(true);
        setError(null);
        try {
            const empty: CompanyLimitOverrides = {
                maxUserMessageTokens: null,
                maxOutputTokens: null,
                maxContextTokens: null,
                dailyTokenLimit: null,
                monthlyTokenLimit: null,
                dailyRequestLimit: null,
            };
            const saved = await saveCompanyAiLimits(id, empty);
            setData((prev) => (prev ? { ...prev, settings: saved } : prev));
            setOverrides(saved.overrides);
            setNotice("This company now uses the platform defaults.");
        } catch (e: unknown) {
            setError(e instanceof Error ? e.message : "Could not reset company limits.");
        } finally {
            setSaving(false);
        }
    };

    const persistAccess = async (hidden: boolean, usernames: string[]): Promise<boolean> => {
        setSavingAccess(true);
        setError(null);
        setNotice(null);
        try {
            const saved = await saveCompanyAssistAccess(id, {
                hidden,
                evaluatorUsernames: usernames,
            });
            setData((prev) => (prev ? { ...prev, settings: saved } : prev));
            setAssistHidden(saved.assistHidden);
            setEvaluators(saved.assistEvaluatorUsernames ?? []);
            setEvaluatorInput("");
            setNotice(assistAccessNotice(saved.assistHidden, saved.assistEvaluatorUsernames ?? []));
            return true;
        } catch (e: unknown) {
            setError(e instanceof Error ? e.message : "Could not save Assist access.");
            return false;
        } finally {
            setSavingAccess(false);
        }
    };

    const addEvaluator = () => {
        const next = mergeAssistReviewers(evaluators, evaluatorInput);
        if (next.length === evaluators.length) {
            setEvaluatorInput("");
            return;
        }
        const previous = evaluators;
        setEvaluators(next);
        setEvaluatorInput("");
        void persistAccess(assistHidden, next).then((ok) => {
            if (!ok) {
                setEvaluators(previous);
            }
        });
    };

    const removeEvaluator = (username: string) => {
        const previous = evaluators;
        const next = evaluators.filter((item) => item !== username);
        setEvaluators(next);
        void persistAccess(assistHidden, next).then((ok) => {
            if (!ok) {
                setEvaluators(previous);
            }
        });
    };

    const saveAccess = async () => {
        await persistAccess(assistHidden, mergeAssistReviewers(evaluators, evaluatorInput));
    };

    const applySettings = (saved: CompanyAiUsage["settings"]) => {
        setData((prev) => (prev ? { ...prev, settings: saved } : prev));
        setWebSearchEnabled(saved.webSearchEnabled);
        setAllowedDomains(saved.allowedDomains ?? []);
    };

    const saveWebSearch = async () => {
        setSavingWebSearch(true);
        setError(null);
        setNotice(null);
        try {
            const saved = await saveCompanyWebSearch(id, {
                inheritPlatform: false,
                webSearchEnabled,
                allowedDomains,
            });
            applySettings(saved);
            setNotice("Web search settings saved. New questions from this organisation use them.");
        } catch (e: unknown) {
            setError(e instanceof Error ? e.message : "Could not save web search settings.");
        } finally {
            setSavingWebSearch(false);
        }
    };

    const resetWebSearch = async () => {
        setSavingWebSearch(true);
        setError(null);
        setNotice(null);
        try {
            const saved = await saveCompanyWebSearch(id, { inheritPlatform: true });
            applySettings(saved);
            setNotice("This organisation now uses the platform web search settings.");
        } catch (e: unknown) {
            setError(e instanceof Error ? e.message : "Could not reset web search settings.");
        } finally {
            setSavingWebSearch(false);
        }
    };

    return (
        <section className="pa-section" data-testid="polaris-company-ai-usage-page">
            <Link to="/polaris-admin/companies" className="pa-back-link">
                Back to companies
            </Link>
            <h2 className="pa-section-title">
                {data ? `${data.settings.companyName} · AI usage` : "Company AI usage"}
            </h2>
            <p className="pa-muted">
                Review token use for this organisation, or choose whether it may search official websites.
            </p>
            {error && <div className="pa-error">{error}</div>}
            {notice && <div className="pa-success">{notice}</div>}

            <div className="pa-tabs" role="tablist" aria-label="Company Assist sections">
                <button
                    type="button"
                    role="tab"
                    aria-selected={tab === "usage"}
                    className={`pa-tab${tab === "usage" ? " active" : ""}`}
                    onClick={() => {
                        setNotice(null);
                        setTab("usage");
                    }}
                >
                    Usage
                </button>
                <button
                    type="button"
                    role="tab"
                    aria-selected={tab === "configuration"}
                    className={`pa-tab${tab === "configuration" ? " active" : ""}`}
                    onClick={() => {
                        setNotice(null);
                        setTab("configuration");
                    }}
                >
                    Configuration
                </button>
            </div>

            {tab === "configuration" ? (
                <form
                    className="pa-assist-card"
                    onSubmit={(e) => {
                        e.preventDefault();
                        void saveWebSearch();
                    }}
                >
                    <div className="pa-assist-card-header">
                        <div>
                            <h3>Official web search</h3>
                            <p className="pa-assist-card-lead">
                                {data?.settings.webSearchUsesPlatformDefaults
                                    ? "Using the platform websites until you save a change for this organisation."
                                    : "These websites apply only to this organisation."}
                            </p>
                        </div>
                    </div>
                    <label className="pa-assist-toggle">
                        <input
                            type="checkbox"
                            checked={webSearchEnabled}
                            onChange={(e) => setWebSearchEnabled(e.target.checked)}
                        />
                        <span>Allow official web search</span>
                    </label>
                    <AssistAllowedDomainsField domains={allowedDomains} onChange={setAllowedDomains} />
                    <div className="pa-assist-actions">
                        <button type="submit" className="pa-page-btn active" disabled={savingWebSearch}>
                            {savingWebSearch ? "Saving…" : "Save configuration"}
                        </button>
                        <button
                            type="button"
                            className="pa-page-btn"
                            disabled={savingWebSearch}
                            onClick={() => void resetWebSearch()}
                        >
                            Use platform defaults
                        </button>
                    </div>
                </form>
            ) : (
                <>
            <div className="pa-assist-card">
                <div className="pa-assist-card-header">
                    <h3>Usage</h3>
                    <div className="pa-period-toggle" role="tablist" aria-label="Cost period">
                        {(["daily", "monthly", "yearly"] as Period[]).map((item) => (
                            <button
                                key={item}
                                type="button"
                                role="tab"
                                aria-selected={period === item}
                                className={`pa-page-btn${period === item ? " active" : ""}`}
                                onClick={() => setPeriod(item)}
                            >
                                {item[0].toUpperCase() + item.slice(1)}
                            </button>
                        ))}
                    </div>
                </div>
                <div className="pa-assist-stats-grid">
                    <div className="stat-card">
                        <span className="stat-label">{periodLabel(period)} tokens</span>
                        <span className="stat-value">{(data?.period.totalTokens ?? 0).toLocaleString()}</span>
                    </div>
                    <div className="stat-card">
                        <span className="stat-label">{periodLabel(period)} cost</span>
                        <span className="stat-value">{formatUsd(data?.period.costUsd)}</span>
                    </div>
                    <div className="stat-card">
                        <span className="stat-label">Lifetime tokens</span>
                        <span className="stat-value">{(data?.lifetime.totalTokens ?? 0).toLocaleString()}</span>
                    </div>
                    <div className="stat-card">
                        <span className="stat-label">Lifetime cost</span>
                        <span className="stat-value">{formatUsd(data?.lifetime.costUsd)}</span>
                    </div>
                </div>
            </div>

            {data && (
                <form
                    className="pa-assist-card"
                    onSubmit={(e) => {
                        e.preventDefault();
                        void saveAccess();
                    }}
                >
                    <div className="pa-assist-card-header">
                        <div>
                            <h3>Who can use Polaris Assist</h3>
                            <p className="pa-assist-card-lead">
                                {assistHidden
                                    ? "Hidden from this organisation. Only reviewers below can open it."
                                    : "Everyone in this organisation can see and use Polaris Assist."}
                            </p>
                        </div>
                        <span
                            className={`pa-assist-status${assistHidden ? " pa-assist-status--hidden" : ""}`}
                        >
                            {assistHidden ? "Hidden" : "Visible"}
                        </span>
                    </div>
                    <label className="pa-assist-toggle">
                        <input
                            type="checkbox"
                            checked={assistHidden}
                            onChange={(e) => setAssistHidden(e.target.checked)}
                        />
                        <span>Hide Polaris Assist for this organisation</span>
                    </label>
                    {assistHidden ? (
                        <div className="pa-assist-reviewers">
                            <p className="pa-assist-reviewers-title">Reviewers</p>
                            <div className="pa-assist-evaluator-row">
                                <input
                                    className="pa-filter-input"
                                    type="text"
                                    value={evaluatorInput}
                                    placeholder="username@school.edu"
                                    aria-label="Reviewer username"
                                    onChange={(e) => setEvaluatorInput(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") {
                                            e.preventDefault();
                                            addEvaluator();
                                        }
                                    }}
                                />
                                <button
                                    type="button"
                                    className="pa-page-btn"
                                    disabled={savingAccess}
                                    onClick={addEvaluator}
                                >
                                    Add
                                </button>
                            </div>
                            {evaluators.length > 0 ? (
                                <ul className="pa-assist-evaluator-list">
                                    {evaluators.map((username) => (
                                        <li key={username} className="pa-assist-evaluator-chip">
                                            <span>{username}</span>
                                            <button
                                                type="button"
                                                className="pa-assist-remove"
                                                aria-label={`Remove reviewer ${username}`}
                                                disabled={savingAccess}
                                                onClick={() => removeEvaluator(username)}
                                            >
                                                Remove
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            ) : (
                                <p className="pa-assist-empty">No reviewers yet. Add a username from this organisation.</p>
                            )}
                        </div>
                    ) : null}
                    <div className="pa-assist-actions">
                        <button type="submit" className="pa-page-btn active" disabled={savingAccess}>
                            {savingAccess ? "Saving…" : "Save who can use Assist"}
                        </button>
                    </div>
                </form>
            )}

            {data && overrides && (
                <form
                    className="pa-assist-card"
                    onSubmit={(e) => {
                        e.preventDefault();
                        void save();
                    }}
                >
                    <div className="pa-assist-card-header">
                        <div>
                            <h3>Usage limits</h3>
                            <p className="pa-assist-card-lead">
                                Leave a box empty to keep the platform default. These limits are per person.
                            </p>
                        </div>
                    </div>
                    <div className="pa-assist-grid">
                        {assistLimitFields("company").map(({ key, label, hint }) => (
                            <label key={key} className="pa-assist-field">
                                <span className="pa-assist-field-top">
                                    <span>{label}</span>
                                    <span className="pa-assist-effective">
                                        In use: {data.settings.effective[key].toLocaleString()}
                                    </span>
                                </span>
                                <input
                                    className="pa-filter-input"
                                    type="number"
                                    min={0}
                                    placeholder={`Default ${data.settings.platform[key].toLocaleString()}`}
                                    value={toInput(overrides[key])}
                                    title={hint}
                                    aria-label={label}
                                    onChange={(e) =>
                                        setOverrides((prev) =>
                                            prev ? { ...prev, [key]: parseOverride(e.target.value) } : prev,
                                        )
                                    }
                                />
                            </label>
                        ))}
                    </div>
                    <div className="pa-assist-actions">
                        <button type="submit" className="pa-page-btn active" disabled={saving}>
                            {saving ? "Saving…" : "Save limits"}
                        </button>
                        <button type="button" className="pa-page-btn" disabled={saving} onClick={() => void resetToPlatform()}>
                            Use platform defaults
                        </button>
                    </div>
                </form>
            )}
                </>
            )}
        </section>
    );
}
